import API, { alphaPath, responseWrapper } from './config'

import type {
  MemoryHistoryResponse,
  MemoryListResponse,
  MemoryStatusResponse,
} from '@/types/memory.type'
import type { OntologyFilterOptions } from '@/types/ontology.type'
import type { AgentActionCardsResponse } from '@/types/agent_action_card.type'
import {
  CompanyPageBatchResults,
  CompanyPageTimelineResponse,
  EarningsHistoryResponse,
  Message,
  MessageStatus,
  SearchProcessItem,
  SearchStockChartData,
  SelectableModel,
  StructuredDataResponse,
  WorkspaceModelOptionsResponse,
} from '@/types/message.type'
import {
  Conversation,
  ConversationFolderListResponse,
  ConversationSortField,
  ConversationSortOrder,
  OnyxChatStatus,
} from '@/types/conversation.type'
import { NewsReference, Reference, SearchStatus, TranslateStatus } from '@/types/reference.type'
import {
  LatestFyfqStatus,
  MarketCapData,
  OnboardingStock,
  Stock,
  StockPriceResponse,
  TickerIndustryMetricInfo,
  TtsqlMetricOption,
} from '@/types/stock'
import { SearchFilter } from '@/types/filter.type'
import { StockDocument } from '@/types/stock_document'
import { EarningsEvent } from '@/types/earnings_event.type'
import { BrokerageConference } from '@/types/brokerage_conference.type'
import { OnyxSource, SessionReferenceViewerData } from '@/types/onyx_server.type'
import { SessionWorkspaceStateResponse } from '@/types/sessionWorkspaceState.type'
import { UserSetting, UserSettingScope } from '@/types/user_setting.type'
import { UserPreference, BehavioralProfilingResult } from '@/types/user_preference.type'
import { InvestmentProfile } from '@/types/investment_profile.type'
import { TaxonomyResponse } from '@/types/investment_taxonomy.type'
import { TrendingTopicsResponse } from '@/types/trending_topic.type'
import { Source, MeetingAgentSource } from '@/types/source'
import { User } from '@/types/user.type'
import {
  ArtifactLibraryItem,
  ArtifactLibraryResponse,
  ArtifactLibraryScope,
  ArtifactShareListResponse,
  LibrarySignUrlResponse,
} from '@/types/artifactShare.type'
import { Organization } from '@/types/organization.type'
import { Invitation } from '@/types/invitation.type'
import { Subscription } from '@/types/subscription.type'
import { HistoricalData } from '@/types/catalyst_search.type'
import { CatalystCache, CatalystCacheType } from '@/types/catalyst_cache.type'
import { BeatMissResponse, StockRealTimePriceData } from '@/types/catalyst.type'
import { Index, IndexType } from '@/types/index.type'
import { ErrorNotification } from '@/types/error_notification.type'
import { RmsReference } from '@/types/rms_reference.type'
import {
  CellReference,
  ResultTable,
  SortDirection,
  TableColumn,
  TableRow,
} from '@/types/table.type'
import { FlagStatus, Perspective, TopicTrend, TrendPlatform } from '@/types/trend.type'
import { Inference, InferenceReference } from '@/types/inference.type'
import { RmsV2FunctionCall } from '@/types/function_call.type'
import { DocumentReference } from '@/types/document_reference.type'
import {
  BriefDeliveriesListResponse,
  BriefDeliveryDetailResponse,
  BriefingFeedbackItemsResponse,
  BriefingFeedbackTokenDeliveryResponse,
  BriefSchedulesListResponse,
  HanwhaBriefDocumentsListResponse,
  HanwhaGenerationStatusResponse,
  HanwhaRedeliverPreviewResponse,
  HanwhaRedeliverResponse,
  HanwhaRegenerateResponse,
  RegenerateAndResendResponse,
} from '@/types/briefing_agent.type'
import { JsonResponse } from '@/types'
import { MeetingAgentEvent, MeetingInformation } from '@/types/meeting_information.type'
import { StorageCollection } from '@/types/rms_v2.type'
import { ApiKey, UsageDashboardData, KeyUsageDashboardData } from '@/types/api_key.type'
import {
  SkillAssetClass,
  SkillWorkflow,
  SkillWorkflowOption,
  WorkspaceSkill,
  WorkspaceSkillMode,
  WorkspaceSkillsResponse,
} from '@/types/workspace_skill.type'
import { SkillGroupsResponse } from '@/types/skill_group.type'
import { FactsetOauthCallbackParams, FactsetOauthStatus } from '@/types/factset_oauth.type'
import {
  MicrosoftConnector,
  MicrosoftOauthCallbackParams,
  MicrosoftOauthStatus,
} from '@/types/microsoft_oauth.type'
import { OntologyClass, OntologyEntity, OntologyProperty } from '@/types/ontology.type'
import {
  ComplianceMeetingSummaryDetail,
  ComplianceMeetingSummaryFilters,
  ComplianceMeetingSummaryListResponse,
} from '@/types/compliance.type'
import {
  Trigger,
  TriggerListResponse,
  TriggerEventListResponse,
  TriggerEventDetailResponse,
  TriggerEventsQuery,
} from '@/types/alert_agent.type'
import type { WebWatchListResponse, WebWatchShowResponse } from '@/types/web_watch.type'
import {
  SlackInstallCallbackParams,
  SlackInstallStatus,
  SlackOauthCallbackParams,
  SlackOauthStatus,
} from '@/types/slack_oauth.type'

// Owner-only, oldest first, 200 a page (alpha's cap); every page is read so a
// long conversation's newest turns are not cut off. No toast on failure: the
// card itself says it could not load.
const AGENT_ACTION_CARD_MAX_PAGES = 20

export const getAgentActionCards = (conversationId: string) => async () => {
  const data: AgentActionCardsResponse['data'] = []
  let page = 1
  while (page > 0 && page <= AGENT_ACTION_CARD_MAX_PAGES) {
    const current = page
    const response = await responseWrapper<AgentActionCardsResponse>(
      () =>
        API.get<AgentActionCardsResponse>(`${alphaPath}/agent_action_cards`, {
          params: { conversation_id: conversationId, page: current, per_page: 200 },
        }),
      false
    )
    data.push(...response.data)
    page = response.next_page
  }
  return { data }
}

export const getBriefSchedules = () => async () => {
  return await responseWrapper<BriefSchedulesListResponse>(() =>
    API.get<BriefSchedulesListResponse>(`${alphaPath}/brief_schedules`)
  )
}

export const getBriefDeliveries =
  (scheduleId: string, page = 1, perPage = 10) =>
  async () => {
    return await responseWrapper<BriefDeliveriesListResponse>(() =>
      API.get<BriefDeliveriesListResponse>(
        `${alphaPath}/brief_schedules/${scheduleId}/deliveries`,
        {
          params: { page, per_page: perPage },
        }
      )
    )
  }

export const getAllBriefDeliveries =
  (page = 1, perPage = 10, query?: string, scheduleId?: string) =>
  async () => {
    return await responseWrapper<BriefDeliveriesListResponse>(() =>
      API.get<BriefDeliveriesListResponse>(`${alphaPath}/brief_deliveries`, {
        params: {
          page,
          per_page: perPage,
          ...(query ? { q: query } : {}),
          ...(scheduleId ? { brief_schedule_id: scheduleId } : {}),
        },
      })
    )
  }

export const getBriefDeliveryDetail = (deliveryId: string) => async () => {
  return await responseWrapper<BriefDeliveryDetailResponse>(() =>
    API.get<BriefDeliveryDetailResponse>(`${alphaPath}/brief_deliveries/${deliveryId}`)
  )
}

// Hanwha CIO Daily Brief "문서(Documents)" tab — document-carrying deliveries
// (hanwha_market_briefing role, enforced server-side).
export interface HanwhaBriefDocumentFilters {
  q?: string
  from?: string // YYYY-MM-DD, delivered_at >= from
  to?: string // YYYY-MM-DD, delivered_at <= to
}

export const getHanwhaBriefDocuments =
  (page = 1, perPage = 10, filters: HanwhaBriefDocumentFilters = {}) =>
  async () => {
    return await responseWrapper<HanwhaBriefDocumentsListResponse>(() =>
      API.get<HanwhaBriefDocumentsListResponse>(`${alphaPath}/brief_deliveries/documents`, {
        params: {
          page,
          per_page: perPage,
          ...(filters.q ? { q: filters.q } : {}),
          ...(filters.from ? { from: filters.from } : {}),
          ...(filters.to ? { to: filters.to } : {}),
        },
      })
    )
  }

// Regenerate + resend a failed Hanwha document delivery. isAlert=false so the
// caller can surface the server's specific 422/409 message instead of the generic toast.
export const regenerateAndResendBriefDocument = (deliveryId: string) => async () => {
  return await responseWrapper<RegenerateAndResendResponse>(
    () =>
      API.post<RegenerateAndResendResponse>(
        `${alphaPath}/brief_deliveries/${deliveryId}/regenerate_and_resend`
      ),
    false
  )
}

// Hanwha CIO ops surface (PRD linq-alpha-server#3496 M2) — generation status,
// on-demand producer regenerate, and same-day redeliver.
export const getHanwhaGenerationStatus = () => async () => {
  return await responseWrapper<HanwhaGenerationStatusResponse>(() =>
    API.get<HanwhaGenerationStatusResponse>(`${alphaPath}/hanwha_briefing/generation_status`)
  )
}

export const getHanwhaRedeliverPreview = () => async () => {
  return await responseWrapper<HanwhaRedeliverPreviewResponse>(() =>
    API.get<HanwhaRedeliverPreviewResponse>(`${alphaPath}/hanwha_briefing/redeliver_preview`)
  )
}

export const regenerateHanwhaProducer = async () => {
  return await responseWrapper<HanwhaRegenerateResponse>(
    () => API.post<HanwhaRegenerateResponse>(`${alphaPath}/hanwha_briefing/regenerate`),
    false
  )
}

export const redeliverHanwha = async () => {
  return await responseWrapper<HanwhaRedeliverResponse>(
    () => API.post<HanwhaRedeliverResponse>(`${alphaPath}/hanwha_briefing/redeliver`),
    false
  )
}

// Phase 1b of PRD #2500 — logged-in highlight feedback list/restore.
export const getBriefingFeedbackItems = (deliveryId: string) => async () => {
  return await responseWrapper<BriefingFeedbackItemsResponse>(() =>
    API.get<BriefingFeedbackItemsResponse>(
      `${alphaPath}/brief_deliveries/${deliveryId}/feedback_items`
    )
  )
}

// Token-anonymous variant: hits the email-token endpoint instead and skips
// auth headers via API.get default config (no JWT required).
// NOTE: bypasses `responseWrapper` for the same reason as
// `createBriefingFeedbackItemAnonymous` above — email recipients aren't in the
// authenticated shell, so the global toast UI is not appropriate. The
// landing page (`/brief_agent/feedback`) renders its own error state from
// the React Query `error` field instead.
export const getBriefingFeedbackTokenDelivery =
  (token: string) => async (): Promise<BriefingFeedbackTokenDeliveryResponse> => {
    const response = await API.get<BriefingFeedbackTokenDeliveryResponse>(
      `${alphaPath}/email_feedbacks/${encodeURIComponent(token)}/delivery`
    )
    return response.data
  }

export const getConversation = (conversation_id?: string, client_temp_id?: string) => async () => {
  return await responseWrapper<Conversation | null>(() =>
    API.get<Conversation | null>(`${alphaPath}/conversations/${conversation_id}`, {
      // Only Analytics opt-in passes `client_temp_id`. Omitting it leaves
      // the server in legacy "shared temp row" mode so RMS v2 and other
      // pages that share the same constant are unaffected. See PRD
      // linq-rag/linq-alpha-client#3248.
      params: client_temp_id ? { client_temp_id } : undefined,
    })
  )
}

export const getRmsConversation = (conversation_id: string) => async () => {
  return await responseWrapper<Conversation>(() =>
    API.get<Conversation>(`${alphaPath}/conversations/${conversation_id}/rms`)
  )
}

// The caller's current Q&A conversation for a shared artifact, or null when
// they have never asked about it. Keyed by artifact_id (a query param, not a
// path segment) since the conversation id is what we are looking up. The
// server returns the newest match — a reset simply opens a newer conversation.
export const getConversationForArtifact = (artifact_id?: string) => async () => {
  return await responseWrapper<Conversation | null>(() =>
    API.get<Conversation | null>(`${alphaPath}/conversations/for_artifact`, {
      params: { artifact_id },
    })
  )
}

export const getConversations =
  (
    page: number,
    start_model?: SelectableModel[],
    q?: string,
    is_starred?: boolean,
    per_page?: number,
    sort_by?: ConversationSortField,
    order?: ConversationSortOrder,
    folder_id?: string
  ) =>
  async () => {
    return await responseWrapper<{
      total: number
      data: Conversation[]
      next_page: number
      // True when the server's body scan hit its statement timeout and this page
      // fell back to title-only results. Pages then come from different match
      // sets, so offset pagination is no longer coherent — stop paging.
      search_degraded?: boolean
    }>(() =>
      API.get<{
        total: number
        data: Conversation[]
        next_page: number
        // True when the server's body scan hit its statement timeout and this page
        // fell back to title-only results. Pages then come from different match
        // sets, so offset pagination is no longer coherent — stop paging.
        search_degraded?: boolean
      }>(`${alphaPath}/conversations`, {
        params: { page, start_model, q, is_starred, per_page, sort_by, order, folder_id },
      })
    )
  }

export const getConversationFolders = (q?: string) => async () => {
  return await responseWrapper<ConversationFolderListResponse>(() =>
    API.get<ConversationFolderListResponse>(`${alphaPath}/conversation_folders`, {
      params: { q, per_page: 100 },
    })
  )
}

// Current-user library scopes and name search, proxied through alpha-server
// (GET /core/users/me/artifacts). Paginated by artifact with next_page null on
// the last page; personal pins are returned in one page.
// By default, only the legacy empty All request suppresses 422; server failures still toast.
export const getArtifactLibrary =
  (scope: ArtifactLibraryScope = 'owned', page = 1, q = '', isAlert = true) =>
  async () => {
    try {
      const response = await API.get<ArtifactLibraryResponse>(`${alphaPath}/users/me/artifacts`, {
        params: { scope, page, q },
      })
      return response.data
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status
      const legacyValidation = scope === 'all' && page === 1 && !q && status === 422
      return responseWrapper<ArtifactLibraryResponse>(
        () => Promise.reject(error),
        isAlert && !legacyValidation
      )
    }
  }

// Memory page (Memory Revamp Phase 1) — proxied through alpha-server
// (/core/users/me/memories). `status` says whether memory_v2 is on for this user.
// isAlert=false: every user's left menu checks this in the background, so a failure must not
// pop the global error toast; the Memory item simply stays hidden.
export const getMemoryStatus = () => async () => {
  return await responseWrapper<MemoryStatusResponse>(
    () => API.get<MemoryStatusResponse>(`${alphaPath}/users/me/memories/status`),
    false
  )
}

export const getMemories = () => async () => {
  return await responseWrapper<MemoryListResponse>(() =>
    API.get<MemoryListResponse>(`${alphaPath}/users/me/memories`)
  )
}

// Removed memories the user can restore (most recently removed first, capped by alpha).
export const getRemovedMemories = () => async () => {
  return await responseWrapper<MemoryListResponse>(() =>
    API.get<MemoryListResponse>(`${alphaPath}/users/me/memories/removed`)
  )
}

export const getMemoryHistory = (memoryId: string) => async () => {
  return await responseWrapper<MemoryHistoryResponse>(() =>
    API.get<MemoryHistoryResponse>(`${alphaPath}/users/me/memories/${memoryId}/history`)
  )
}

// Owner-authorized sign-url for the library viewer (POST, no conversation
// context — agent-server authorizes by owner id). Returns the presigned GET
// URL + artifact type for the renderer.
export const getLibraryArtifactSignUrl =
  (artifactId: string, config?: { signal?: AbortSignal; timeoutMs?: number; versionNo?: number }) =>
  async () => {
    return await responseWrapper<LibrarySignUrlResponse>(() =>
      API.post<LibrarySignUrlResponse>(
        `${alphaPath}/artifacts/${artifactId}/library_sign_url`,
        null,
        // Bounded + cancellable so a hung mint can't wedge a caller (e.g. the
        // live-poll tick); the axios instance otherwise defaults to a 5-min timeout.
        {
          signal: config?.signal,
          timeout: config?.timeoutMs,
          // Omitted (not null) when following latest — an empty value would
          // reach the server as a malformed version selector.
          params: config?.versionNo !== undefined ? { version_no: config.versionNo } : undefined,
        }
      )
    )
  }

// Single-artifact metadata for the standalone viewer (show page) — proxied
// through alpha-server (GET /core/artifacts/:id). agent-server authorizes by
// owner id OR an active share grant; alpha enriches with `conversation_id`.
// Returns the same `ArtifactLibraryItem` shape as the library list.
export const getArtifactMetadata = (artifactId: string) => async () => {
  // isAlert=false: metadata is silent enrichment for the show-page header — a
  // 5xx/network blip must NOT pop the global error toast (the body still loads
  // from the separate sign-url query). Error is still thrown, so the hook's
  // retry/isError still run and the header actions simply stay hidden.
  return await responseWrapper<ArtifactLibraryItem>(
    () => API.get<ArtifactLibraryItem>(`${alphaPath}/artifacts/${artifactId}`),
    false
  )
}

// Current recipients of an artifact (owner-only — the share-management view).
export const getArtifactShareRecipients = (artifactId: string) => async () => {
  return await responseWrapper<ArtifactShareListResponse>(() =>
    API.get<ArtifactShareListResponse>(`${alphaPath}/artifacts/${artifactId}/shares`)
  )
}

export const getConversationSources = (id?: string) => async () => {
  return await responseWrapper<Source[]>(() =>
    API.get<Source[]>(`${alphaPath}/conversations/${id}/sources`)
  )
}

export const getConversationOnyxChatStatus = (id?: string, message_id?: string) => async () => {
  return await responseWrapper<{
    data: OnyxChatStatus
  }>(() =>
    API.get<{ data: OnyxChatStatus }>(`${alphaPath}/conversations/${id}/check_onyx_chat_status`, {
      params: { message_id },
    })
  )
}

export const getMessages = (page: number, conversation_id?: string) => async () => {
  return await responseWrapper<{ total: number; data: Message[] }>(() =>
    API.get<{ total: number; data: Message[] }>(
      `${alphaPath}/conversations/${conversation_id}/messages`,
      {
        params: { page, conversation_id },
      }
    )
  )
}

export const getMessage = async (
  messageId: string,
  includeWorkflowResult?: boolean
): Promise<Message> => {
  const { data } = await API.get(`${alphaPath}/messages/${messageId}`, {
    params: { include_workflow_result: includeWorkflowResult },
  })
  return data
}

export const getMessageList = (page: number, conversation_id?: string) => async () => {
  return await responseWrapper<{ total: number; data: Message[]; next_page: number }>(() =>
    API.get<{ total: number; data: Message[]; next_page: number }>(
      `${alphaPath}/conversations/${conversation_id}/messages/list`,
      {
        params: { page, conversation_id },
      }
    )
  )
}

export const getNestedMessages = (rootMessageId?: string) => async () => {
  return await responseWrapper<Message[]>(() =>
    API.get<Message[]>(`${alphaPath}/messages/${rootMessageId}/nested_messages`)
  )
}

export const getSearchResult =
  (message_id?: string, history_id?: string, is_search_delayed?: boolean) => async () => {
    return await responseWrapper<{
      status: SearchStatus
      filters: SearchFilter
      translate_history_id: string
    }>(() =>
      API.get<{ status: SearchStatus; filters: SearchFilter; translate_history_id: string }>(
        `${alphaPath}/references/search_result`,
        {
          params: { message_id, history_id, is_search_delayed },
        }
      )
    )
  }

export const getIntegrateSearchResult =
  (message_id?: string, is_search_delayed?: boolean) => async () => {
    return await responseWrapper<{
      status: MessageStatus
      message_sources: Source[]
      search_process: SearchProcessItem[]
      stock_chart_data: SearchStockChartData[]
    }>(
      () =>
        API.get<{
          status: MessageStatus
          message_sources: Source[]
          search_process: SearchProcessItem[]
          stock_chart_data: SearchStockChartData[]
        }>(`${alphaPath}/references/integrate_search_result_v2`, {
          params: { message_id, is_search_delayed },
        }),
      false
    )
  }

export const getRenewalSearchResult =
  (message_id?: string, history_id?: string, is_search_delayed?: boolean) => async () => {
    return await responseWrapper<{
      status: SearchStatus
      filters: SearchFilter
      translate_history_id: string
    }>(() =>
      API.get<{ status: SearchStatus; filters: SearchFilter; translate_history_id: string }>(
        `${alphaPath}/references/renewal_search_result`,
        {
          params: { message_id, history_id, is_search_delayed },
        }
      )
    )
  }

export const getRmsSearchResult =
  (message_id?: string, history_id?: string, is_search_delayed?: boolean) => async () => {
    return await responseWrapper<{
      status: SearchStatus
    }>(() =>
      API.get<{ status: SearchStatus }>(`${alphaPath}/rms_references/search_result`, {
        params: { message_id, history_id, is_search_delayed },
      })
    )
  }

export const getTranslateResult = (message_id?: string, history_id?: string) => async () => {
  return await responseWrapper<{
    status: TranslateStatus
  }>(() =>
    API.get<{ status: TranslateStatus }>(`${alphaPath}/references/translate_result`, {
      params: { message_id, history_id },
    })
  )
}

export const getReferences = (message_id?: string) => async () => {
  return await responseWrapper<Reference[]>(() =>
    API.get<Reference[]>(`${alphaPath}/messages/${message_id}/references`)
  )
}

export const getReference = (reference_id?: string) => async () => {
  return await responseWrapper<Reference>(() =>
    API.get<Reference>(`${alphaPath}/references/${reference_id}`)
  )
}

export const getNewsReferences = (message_id?: string) => async () => {
  return await responseWrapper<NewsReference[]>(() =>
    API.get<NewsReference[]>(`${alphaPath}/messages/${message_id}/news_references`)
  )
}

export const getRmsReferences = (message_id?: string) => async () => {
  return await responseWrapper<RmsReference[]>(() =>
    API.get<RmsReference[]>(`${alphaPath}/messages/${message_id}/rms_references`)
  )
}

export const getRmsReference = (id?: string) => async () => {
  return await responseWrapper<RmsReference>(() =>
    API.get<RmsReference>(`${alphaPath}/rms_references/${id}`)
  )
}

export const getStocks = (page: number, query?: string) => async () => {
  return await responseWrapper<{ total: number; data: Stock[]; next_page: number }>(() =>
    API.get<{ total: number; data: Stock[]; next_page: number }>(`${alphaPath}/stocks/list`, {
      params: { page, query },
    })
  )
}

export const getStock = (id: string) => async () => {
  return await responseWrapper<Stock>(() => API.get<Stock>(`${alphaPath}/stocks/${id}`))
}

export const getMessageStockDetails = (message_id: string) => async () => {
  return await responseWrapper<Stock>(() =>
    API.get<Stock>(`${alphaPath}/stocks/message_stock_details`, {
      params: { message_id },
    })
  )
}

// FactSet Symbology API - Get company name, exchange, and currency for given ticker_regions
export interface FactsetSymbologyData {
  requestId: string
  name?: string
  frefListingExchange?: string // 3-digit exchange code: NYS, NAS, etc.
  currency?: string
}

export interface FactsetSymbologyResponse {
  data: FactsetSymbologyData[]
}

export const getFactsetSymbology = (tickerRegions: string) => async () => {
  return await responseWrapper<FactsetSymbologyResponse>(() =>
    API.get<FactsetSymbologyResponse>(`${alphaPath}/stocks/factset_symbology`, {
      params: { ticker_regions: tickerRegions },
    })
  )
}

// FactSet Prices API - Get stock prices with currency for a given ticker_region
export interface FactsetPriceData {
  date: string
  price?: number // close price
  priceOpen?: number
  priceHigh?: number
  priceLow?: number
  volume?: number
  currency?: string
  requestId: string
  fsymId?: string
}

export interface FactsetPricesResponse {
  data: FactsetPriceData[]
}

export const getFactsetPrices =
  (tickerRegionFactset: string, startDate?: string, endDate?: string) => async () => {
    return await responseWrapper<FactsetPricesResponse>(() =>
      API.get<FactsetPricesResponse>(`${alphaPath}/stocks/factset_prices`, {
        params: {
          ticker_region_factset: tickerRegionFactset,
          start_date: startDate,
          end_date: endDate,
        },
      })
    )
  }

export const getStocksOnboarding = () => async () => {
  return await responseWrapper<OnboardingStock[]>(() =>
    API.get<OnboardingStock[]>(`${alphaPath}/stocks/onboarding`)
  )
}

export const getSubscribedStocks = () => async () => {
  return await responseWrapper<Stock[]>(() => API.get<Stock[]>(`${alphaPath}/stocks/subscribed`))
}

export const getSubscriptions =
  (sector?: string, query?: string, page?: number, groupId?: string) => async () => {
    return await responseWrapper<{ total: number; data: Subscription[] }>(() =>
      API.get<{ total: number; data: Subscription[] }>(`${alphaPath}/subscriptions`, {
        params: { sector, query, page, group_id: groupId },
      })
    )
  }

export const getSubscriptionsOptionList = (query?: string, page?: number) => async () => {
  return await responseWrapper<{ total: number; data: Subscription[]; next_page: number }>(() =>
    API.get<{ total: number; data: Subscription[]; next_page: number }>(
      `${alphaPath}/subscriptions/option_list`,
      {
        params: { query, page },
      }
    )
  )
}

export const getStockDocuments = (tickers?: string[]) => async () => {
  return await responseWrapper<StockDocument[]>(() =>
    API.get<StockDocument[]>(`${alphaPath}/stock_documents/list`, {
      params: { tickers },
    })
  )
}

export const getStockDocument = (id: string) => async () => {
  return await responseWrapper<StockDocument>(() =>
    API.get<StockDocument>(`${alphaPath}/stock_documents/${id}`)
  )
}

export const getEarningsEvents = (stock_id?: string, page?: number) => async () => {
  return await responseWrapper<{ total: number; data: EarningsEvent[]; next_page: number }>(() =>
    API.get<{ total: number; data: EarningsEvent[]; next_page: number }>(
      `${alphaPath}/earnings_events`,
      {
        params: { page, stock_id },
      }
    )
  )
}

export const getBrokerageConferences = (start_date: string, end_date: string) => async () => {
  return await responseWrapper<BrokerageConference[]>(() =>
    API.get<BrokerageConference[]>(`${alphaPath}/brokerage_conferences`, {
      params: { start_date, end_date },
    })
  )
}

export const getCalendarEarningsEvents =
  (start_date: string, end_date: string, exclude_brokerage_conferences: boolean) => async () => {
    return await responseWrapper<EarningsEvent[]>(() =>
      API.get<EarningsEvent[]>(`${alphaPath}/earnings_events/calendar`, {
        // Broker sessions are drawn as conference bands instead of one card
        // each, so the grid leaves them out — but only while the bands are
        // there to draw. If that query failed they stay, or they would be on
        // no screen at all.
        params: { start_date, end_date, exclude_brokerage_conferences },
      })
    )
  }

export const getMonthlyEarningsEvents =
  (page: number, start_date: string, end_date: string) => async () => {
    return await responseWrapper<{ total: number; data: EarningsEvent[] }>(() =>
      API.get<{ total: number; data: EarningsEvent[] }>(`${alphaPath}/earnings_events/monthly`, {
        params: { page, start_date, end_date },
      })
    )
  }

export const getEarningsEvent = (earnings_event_id?: string) => async () => {
  return await responseWrapper<EarningsEvent>(() =>
    API.get<EarningsEvent>(`${alphaPath}/earnings_events/${earnings_event_id}`)
  )
}

export const getUserSettings = (scope: UserSettingScope) => async () => {
  return await responseWrapper<UserSetting[]>(() =>
    API.get<UserSetting[]>(`${alphaPath}/user_settings`, {
      params: { setting_scope: scope },
    })
  )
}

export const getInvestmentProfile = () => async () => {
  return await responseWrapper<InvestmentProfile>(() =>
    API.get<InvestmentProfile>(`${alphaPath}/investment_profile`)
  )
}

export const getUserPreference = () => async () => {
  return await responseWrapper<UserPreference>(() =>
    API.get<UserPreference>(`${alphaPath}/user_preference`)
  )
}

export const getInvestmentTaxonomy = () => async () => {
  return await responseWrapper<TaxonomyResponse>(() =>
    API.get<TaxonomyResponse>(`${alphaPath}/investment_taxonomy`)
  )
}

export const checkBehavioralProfiling = () => async () => {
  return await responseWrapper<BehavioralProfilingResult>(() =>
    API.get<BehavioralProfilingResult>(`${alphaPath}/behavioral_profiling/check`)
  )
}

export const getTrendingTopics = () => async () => {
  return await responseWrapper<TrendingTopicsResponse>(() =>
    API.get<TrendingTopicsResponse>(`${alphaPath}/trending_topics`)
  )
}

export const patchUserOnboardingFalse = () => async () => {
  return await responseWrapper<User>(() =>
    API.patch<User>(`${alphaPath}/users/update_earnings_management_onboarding`)
  )
}

// membersOnly narrows to the seat roster (Settings > Members). The other callers pick a
// person and must keep seeing Linq staff seated in the org.
export const getOrganizationUsers =
  (page: number, membersOnly = false) =>
  async () => {
    return await responseWrapper<{ total: number; data: User[]; next_page: number }>(() =>
      API.get<{ total: number; data: User[]; next_page: number }>(`${alphaPath}/users`, {
        params: membersOnly ? { page, members_only: true } : { page },
      })
    )
  }

export const getCurrentUser = async () => API.get<JsonResponse<User>>(`${alphaPath}/users/me`)

export const getSources = (page: number) => async () => {
  return await responseWrapper<{ total: number; data: Source[]; next_page: number }>(() =>
    API.get<{ total: number; data: Source[]; next_page: number }>(`${alphaPath}/sources`, {
      params: { page },
    })
  )
}

export const getSource = (id?: string) => async () => {
  return await responseWrapper<Source>(() => API.get<Source>(`${alphaPath}/sources/${id}`))
}

export const getSourceDocument = (id?: string) => async () => {
  return await responseWrapper<Source>(() => API.get<Source>(`${alphaPath}/sources/${id}/document`))
}

export const getOrganization = (id?: string | null) => async () => {
  return await responseWrapper<Organization>(() =>
    API.get<Organization>(`${alphaPath}/organizations/${id}`)
  )
}

export const getInvitations = () => async () => {
  return await responseWrapper<Invitation[]>(() =>
    API.get<Invitation[]>(`${alphaPath}/invitations`)
  )
}

export const getHistoricalData = (ticker: string, from: string, to: string) => async () => {
  return await responseWrapper<HistoricalData[]>(() =>
    API.get<HistoricalData[]>(`${alphaPath}/fmps/historical_price_full`, {
      params: { ticker, from, to },
    })
  )
}

export const getStockPriceData =
  (
    message_id: string,
    stock_id: string,
    ticker: string,
    from: string,
    to: string,
    signal?: AbortSignal
  ) =>
  async () => {
    return await responseWrapper<HistoricalData[]>(() =>
      API.get<HistoricalData[]>(`${alphaPath}/fmps/stock_prices`, {
        params: { message_id, stock_id, ticker, from, to },
        signal,
      })
    )
  }

export const getRealTimePriceData = (ticker: string) => async () => {
  return await responseWrapper<StockRealTimePriceData>(() =>
    API.get<StockRealTimePriceData>(`${alphaPath}/fmps/real_time_price`, {
      params: { ticker },
    })
  )
}

export const getStockPriceChangeData = (ticker: string) => async () => {
  return await responseWrapper<Record<string, any>>(() =>
    API.get<Record<string, any>>(`${alphaPath}/fmps/stock_price_change`, {
      params: { ticker },
    })
  )
}

export const getFmpStockByStockServerId = (stock_id: string) => async () => {
  return await responseWrapper<Record<string, any>>(() =>
    API.get<Record<string, any>>(`${alphaPath}/fmps/search_isin_by_stock_id`, {
      params: { stock_id },
    })
  )
}

export const getFmpSymbolByStockServerId =
  (message_id: string, stock_id: string, exchange: string, fmp_symbol?: string, name?: string) =>
  async () => {
    return await responseWrapper<Record<string, any>>(() =>
      API.get<Record<string, any>>(`${alphaPath}/fmps/search_symbol_by_stock_id`, {
        params: { message_id, stock_id, exchange, fmp_symbol, name },
      })
    )
  }

export const getCatalystCache =
  (stockId: string, cache_type?: CatalystCacheType, start_date?: string, end_date?: string) =>
  async () => {
    return await responseWrapper<CatalystCache>(() =>
      API.get<CatalystCache>(`${alphaPath}/catalyst_caches/${stockId}`, {
        params: { cache_type, start_date, end_date },
      })
    )
  }

export const getEarningsBeatMiss =
  (stockId: string, start_date?: string, end_date?: string) => async () => {
    return await responseWrapper<BeatMissResponse>(() =>
      API.get<BeatMissResponse>(`${alphaPath}/stocks/${stockId}/earnings_beat_miss`, {
        params: { start_date, end_date },
      })
    )
  }

export const getConsensusData =
  (message_id: string, stock_id: string, start_date?: string, end_date?: string) => async () => {
    return await responseWrapper<BeatMissResponse>(() =>
      API.get<BeatMissResponse>(`${alphaPath}/stocks/${stock_id}/consensus_data`, {
        params: { message_id, start_date, end_date },
      })
    )
  }

export const getUserIndex = (indexType?: IndexType) => async () => {
  return await responseWrapper<Index>(() =>
    API.get<Index>(`${alphaPath}/indices/me`, {
      params: { index_type: indexType },
    })
  )
}

export const getReleasedErrorNotifications = () => async () => {
  return await responseWrapper<ErrorNotification[]>(() =>
    API.get<ErrorNotification[]>(`${alphaPath}/error_notifications?status=released`)
  )
}

export const getOwnershipCsvFile = (table_name: string, filters: SearchFilter) => async () => {
  return await responseWrapper<Blob>(() =>
    API.get<Blob>(`${alphaPath}/messages/download_ownership_data`, {
      params: {
        table_name,
        per: 2000,
        order_key: 'position',
        transaction_order_key: 'transaction_date',
        order_direction: 'desc',
        filters,
      },
      responseType: 'blob',
    })
  )
}

export const getMessageResultTables = (id: string) => async () => {
  return await responseWrapper<ResultTable[]>(() =>
    API.get<ResultTable[]>(`${alphaPath}/messages/${id}/table_data`)
  )
}

export const getMessageStockPrices =
  (
    id: string,
    stock_id: string,
    start_date: string,
    end_date: string,
    limit?: number,
    signal?: AbortSignal
  ) =>
  async () => {
    return await responseWrapper<StockPriceResponse>(() =>
      API.get<StockPriceResponse>(`${alphaPath}/messages/${id}/stock_prices`, {
        params: { stock_id, start_date, end_date, limit },
        signal,
      })
    )
  }

export const getStructuredData =
  (id: string, stock_id: string, data_type: string, signal?: AbortSignal) => async () => {
    return await responseWrapper<StructuredDataResponse>(() =>
      API.get<StructuredDataResponse>(`${alphaPath}/messages/${id}/execute_structured_data`, {
        params: { stock_id, data_type },
        signal,
      })
    )
  }

export const getTableRows =
  (
    message_id?: string,
    result_table_id?: string,
    order_key?: string,
    order_direction?: SortDirection,
    order_data_type?: string,
    filter?: Record<string, any>
  ) =>
  async () => {
    return await responseWrapper<TableRow[]>(() =>
      API.get<TableRow[]>(`${alphaPath}/table_rows`, {
        params: {
          message_id,
          result_table_id,
          order_key,
          order_direction,
          order_data_type,
          filter,
        },
      })
    )
  }

export const checkMessageColumn =
  (id: string, column_ids?: string[], last_polled_counts?: { [key: string]: number }) =>
  async () => {
    return await responseWrapper<{ columns: TableColumn[]; rows: TableRow[] }>(() =>
      API.get<{ columns: TableColumn[]; rows: TableRow[] }>(
        `${alphaPath}/messages/${id}/check_column`,
        { params: { column_ids, last_polled_counts } }
      )
    )
  }

export const getCellReference = (id?: string) => async () => {
  return await responseWrapper<CellReference>(() =>
    API.get<CellReference>(`${alphaPath}/cell_references/${id}`)
  )
}

export const findCellReference =
  (table_column_id?: string, table_row_id?: string, idx?: number) => async () => {
    return await responseWrapper<CellReference>(() =>
      API.get<CellReference>(`${alphaPath}/cell_references/find_data`, {
        params: {
          table_column_id,
          table_row_id,
          idx,
        },
      })
    )
  }

export const getScreenerCsvFile = (id: string) => async () => {
  return await responseWrapper<Blob>(() =>
    API.get<Blob>(`${alphaPath}/messages/${id}/download_screener`, {
      responseType: 'blob',
    })
  )
}

export const pingTopicTrends = (topic_id: string, platform: TrendPlatform) => async () => {
  return await responseWrapper<FlagStatus>(() =>
    API.get<FlagStatus>(`${alphaPath}/topics/${topic_id}/ping`, {
      params: { platform },
    })
  )
}

export const getTopicTrends =
  (topic_id: string, platform: TrendPlatform, start_date: string, end_date: string) => async () => {
    return await responseWrapper<TopicTrend[]>(() =>
      API.get<TopicTrend[]>(`${alphaPath}/topics/${topic_id}/topic_trends`, {
        params: { platform, start_date, end_date },
      })
    )
  }

export const getTrendHotTopics = (keyword?: string, country?: string) => async () => {
  return await responseWrapper<Perspective[]>(() =>
    API.get<Perspective[]>(`${alphaPath}/topics/perspectives`, {
      params: { keyword, country },
    })
  )
}

export const getMeetingAgentSource = (id: string) => async () => {
  return await responseWrapper<MeetingAgentSource>(() =>
    API.get<MeetingAgentSource>(`${alphaPath}/meeting_informations/${id}/source`)
  )
}

export const getInferences = (message_id?: string) => async () => {
  return await responseWrapper<Inference[]>(() =>
    API.get<Inference[]>(`${alphaPath}/inferences`, { params: { message_id } })
  )
}

export const getFunctionCalls = (inference_id?: string) => async () => {
  return await responseWrapper<RmsV2FunctionCall[]>(() =>
    API.get<RmsV2FunctionCall[]>(`${alphaPath}/function_calls`, { params: { inference_id } })
  )
}

export const getInferenceReferences = (message_id?: string) => async () => {
  return await responseWrapper<InferenceReference[]>(() =>
    API.get<InferenceReference[]>(`${alphaPath}/inference_references`, { params: { message_id } })
  )
}

export const getInferenceReference = (id?: string) => async () => {
  return await responseWrapper<InferenceReference>(() =>
    API.get<InferenceReference>(`${alphaPath}/inference_references/${id}`)
  )
}

export const getTickerIndustryMetric = (ids?: string[]) => async () => {
  return await responseWrapper<TickerIndustryMetricInfo[]>(() =>
    API.get<TickerIndustryMetricInfo[]>(`${alphaPath}/stocks/industry_metrics`, {
      params: { ids },
    })
  )
}

export const getTtsqlMetricOptions = (ids?: string[]) => async () => {
  return await responseWrapper<TtsqlMetricOption[]>(() =>
    API.get<TtsqlMetricOption[]>(`${alphaPath}/agent_v2/execute_ttsql_metric_filter`, {
      params: { ids },
    })
  )
}

export const getDocumentReference = (message_id: string) => async () => {
  return await responseWrapper<DocumentReference>(() =>
    API.get<DocumentReference>(`${alphaPath}/messages/${message_id}/document_references`)
  )
}

export const getDocsPresignedUrl = (id: string) => async () => {
  return await responseWrapper<any>(() =>
    API.get<any>(`${alphaPath}/stock_documents/${id}/presigned_url`)
  )
}

export const getRmsV2PresignedUrl = (s3_file_key: string, organization_id: string) => async () => {
  return await responseWrapper<any>(() =>
    API.get<any>(`${alphaPath}/rms_v2/presigned_url`, {
      params: { s3_file_key, organization_id },
    })
  )
}

export const getRmsV2InfRefByChunk =
  (origin_chunk_url: string, organization_id: string) => async () => {
    return await responseWrapper<any>(() =>
      API.get<any>(`${alphaPath}/rms_v2/inference_reference_by_chunk`, {
        params: {
          origin_chunk_url,
          organization_id,
        },
      })
    )
  }

export const getOnyxReference = (chat_message_id: string, citation_hash: string) => async () => {
  return await responseWrapper<any>(() =>
    API.get<any>(`${alphaPath}/rms_v2/onyx_reference`, {
      params: {
        chat_message_id,
        citation_hash,
      },
    })
  )
}

export const getOnyxReferences = (chat_message_id: string) => async () => {
  return await responseWrapper<{ references: OnyxSource[] }>(() =>
    API.get<{ references: OnyxSource[] }>(`${alphaPath}/onyx/references`, {
      params: {
        chat_message_id,
      },
    })
  )
}

// 북미 리전 agent-server로 고정한 전용 엔드포인트
// 일반 onyx/references 와 응답 형태는 동일하다.
export const getCompanyPageReferences = (chat_message_id: string) => async () => {
  return await responseWrapper<{ references: OnyxSource[] }>(() =>
    API.get<{ references: OnyxSource[] }>(`${alphaPath}/agent_v2/company_page_references`, {
      params: {
        chat_message_id,
      },
    })
  )
}

// 북미 리전 agent-server로 고정한 전용 엔드포인트
// 일반 rms_v2/onyx_reference 와 응답 형태는 동일하다.
export const getCompanyPageReference =
  (chat_message_id: string, citation_hash: string) => async () => {
    return await responseWrapper<any>(() =>
      API.get<any>(`${alphaPath}/agent_v2/company_page_reference`, {
        params: {
          chat_message_id,
          citation_hash,
        },
      })
    )
  }

export const getConversationChatSession = (conversation_id: string) => async () => {
  return await responseWrapper<{ onyx_chat_session_id: string }>(() =>
    API.get<{ onyx_chat_session_id: string }>(`${alphaPath}/agent_v2/conversation_chat_session`, {
      params: { conversation_id },
    })
  )
}

// view=workspace: the web client's view. Without it the server returns the public API's fixed response.
export const getSessionReferences = (chat_session_id: string) => async () => {
  return await responseWrapper<{ references: OnyxSource[] }>(() =>
    API.get<{ references: OnyxSource[] }>(`${alphaPath}/agent_v2/session_references`, {
      params: { chat_session_id, view: 'workspace' },
    })
  )
}

export const getSessionReference = (chat_session_id: string, citation_idx: string) => async () => {
  return await responseWrapper<SessionReferenceViewerData>(() =>
    API.get<SessionReferenceViewerData>(`${alphaPath}/agent_v2/session_reference`, {
      params: { chat_session_id, citation_idx },
    })
  )
}

export const getSessionWorkspaceState = (chat_session_id: string) => async () => {
  return await responseWrapper<SessionWorkspaceStateResponse>(() =>
    API.get<SessionWorkspaceStateResponse>(`${alphaPath}/agent_v2/session_workspace_state`, {
      params: { chat_session_id },
    })
  )
}

export const getCalendlyMeetings = (start_date: string, end_date: string) => async () => {
  return await responseWrapper<MeetingAgentEvent[]>(() =>
    API.get<MeetingAgentEvent[]>(`${alphaPath}/meeting_informations/calendar`, {
      params: {
        start_date,
        end_date,
      },
    })
  )
}

export const getMeetingInformation = (id: string) => async () => {
  return await responseWrapper<MeetingInformation>(() =>
    API.get<MeetingInformation>(`${alphaPath}/meeting_informations/${id}`)
  )
}

export const getIsFinancialCompany = (messageId: string, signal?: AbortSignal) => async () => {
  return await responseWrapper<boolean>(() =>
    API.get<boolean>(`${alphaPath}/agent_v2/check_is_financial`, {
      params: {
        message_id: messageId,
      },
      signal,
    })
  )
}

export const getCompanyPageBatchResults =
  (
    message_id: string,
    quarter: number,
    year: number,
    is_latest?: boolean,
    calendar_date?: string,
    country?: string,
    lang?: string,
    signal?: AbortSignal
  ) =>
  async () => {
    return await responseWrapper<CompanyPageBatchResults>(() =>
      API.get<CompanyPageBatchResults>(
        `${alphaPath}/messages/${message_id}/company_page_batch_results`,
        {
          params: {
            quarter,
            year,
            is_latest,
            calendar_date,
            country,
            lang,
          },
          signal,
        }
      )
    )
  }

export const getEarningsHistoryStatus =
  (message_id: string, lang?: string, signal?: AbortSignal) => async () => {
    return await responseWrapper<EarningsHistoryResponse>(() =>
      API.get<EarningsHistoryResponse>(`${alphaPath}/agent_v2/earnings_history_status`, {
        params: { message_id, lang },
        signal,
      })
    )
  }

export const getCompanyPageTimeline =
  (message_id: string, start_date: string, end_date: string, signal?: AbortSignal) => async () => {
    return await responseWrapper<CompanyPageTimelineResponse>(() =>
      API.post<CompanyPageTimelineResponse>(
        `${alphaPath}/agent_v2/company_page_timeline`,
        { message_id, start_date, end_date },
        { signal }
      )
    )
  }

export const getMarketCap = (message_id: string, stock_id: string, symbol: string) => async () => {
  return await responseWrapper<MarketCapData>(() =>
    API.get<MarketCapData>(`${alphaPath}/fmps/market_cap`, {
      params: {
        message_id,
        stock_id,
        symbol,
      },
    })
  )
}

export const checkLatestFyFq = (stock_id: string) => async () => {
  return await responseWrapper<boolean>(() =>
    API.get<boolean>(`${alphaPath}/stocks/check_latest_fyfq`, {
      params: { stock_id },
    })
  )
}

export const checkLatestFyfqStatus = (stock_id: string) => async () => {
  return await responseWrapper<LatestFyfqStatus>(() =>
    API.get<LatestFyfqStatus>(`${alphaPath}/stocks/check_latest_fyfq_status`, {
      params: { stock_id },
    })
  )
}

export const getLatestFyFq = (message_id: string, signal?: AbortSignal) => async () => {
  return await responseWrapper<{
    stock_id: string
    calendar_date: string
    fiscal_year: number
    fiscal_quarter: number
    country: string
  }>(() =>
    API.get<{
      stock_id: string
      calendar_date: string
      fiscal_year: number
      fiscal_quarter: number
      country: string
    }>(`${alphaPath}/stocks/latest_fyfq`, {
      params: { message_id },
      signal,
    })
  )
}

export const getStockFyFq = (message_id: string) => async () => {
  return await responseWrapper<{
    stock_id: string
    fiscal_quarters: {
      fiscal_year: number
      fiscal_quarter: number
      calendar_date: string
      earnings_release_date: string
    }[]
  }>(() =>
    API.get<{
      stock_id: string
      fiscal_quarters: {
        fiscal_year: number
        fiscal_quarter: number
        calendar_date: string
        earnings_release_date: string
      }[]
    }>(`${alphaPath}/stocks/fyfq_data`, {
      params: {
        message_id,
      },
    })
  )
}

export const getOwnershipInformation =
  (message_id: string, stock_id: string, signal?: AbortSignal) => async () => {
    return await responseWrapper<any>(() =>
      API.get<any>(`${alphaPath}/stocks/${stock_id}/ownership_information`, {
        params: { message_id },
        signal,
      })
    )
  }

export const getSharesOutstanding =
  (message_id: string, stock_id: string, signal?: AbortSignal) => async () => {
    return await responseWrapper<any>(() =>
      API.get<any>(`${alphaPath}/stocks/${stock_id}/shares_outstanding`, {
        params: { message_id },
        signal,
      })
    )
  }

export const getFloatData =
  (message_id: string, stock_id: string, signal?: AbortSignal) => async () => {
    return await responseWrapper<any>(() =>
      API.get<any>(`${alphaPath}/stocks/${stock_id}/float_data`, {
        params: { message_id },
        signal,
      })
    )
  }

export const getOwnershipInsiderTotalPosition =
  (message_id: string, stock_id: string, signal?: AbortSignal) => async () => {
    return await responseWrapper<any>(() =>
      API.get<any>(`${alphaPath}/stocks/${stock_id}/ownership_insider_total_position`, {
        params: { message_id },
        signal,
      })
    )
  }

export const getOwnershipInstitutionalTotalPosition =
  (message_id: string, stock_id: string, signal?: AbortSignal) => async () => {
    return await responseWrapper<any>(() =>
      API.get<any>(`${alphaPath}/stocks/${stock_id}/ownership_institutional_total_position`, {
        params: { message_id },
        signal,
      })
    )
  }

// Storage Collections
interface StorageCollectionsResponse {
  data: StorageCollection[]
  total: number
  page: number
  per_page: number
  has_next: boolean
}

export const getStorageCollections =
  (params?: { page?: number; per_page?: number; q?: string }) => async () => {
    return await responseWrapper<StorageCollectionsResponse>(() =>
      API.get<StorageCollectionsResponse>(`${alphaPath}/storage_collections`, { params })
    )
  }

export const getStorageCollection = (id: string) => async () => {
  return await responseWrapper<StorageCollection>(() =>
    API.get<StorageCollection>(`${alphaPath}/storage_collections/${id}`)
  )
}

// API Keys
export const getApiKeys = () => async () => {
  return await responseWrapper<ApiKey[]>(() => API.get<ApiKey[]>(`${alphaPath}/api_keys`))
}

export const getApiKeyUsage = (range: string) => async () => {
  return await responseWrapper<UsageDashboardData>(() =>
    API.get<UsageDashboardData>(`${alphaPath}/api_keys/usage`, { params: { range } })
  )
}

export const getApiKeyDetailUsage = (id: number, range: string) => async () => {
  return await responseWrapper<KeyUsageDashboardData>(() =>
    API.get<KeyUsageDashboardData>(`${alphaPath}/api_keys/${id}/usage`, { params: { range } })
  )
}

// Workspace Skills
export const getWorkspaceSkills =
  (params?: {
    scope?: string
    page?: number
    per_page?: number
    q?: string
    active_only?: boolean
    // Agent mode the skills must be runnable in (chat/briefing/trigger).
    // Omitted = chat: the server scopes official skills to the mode's DPC profile(s).
    mode?: WorkspaceSkillMode
    // Library taxonomy filters (PRD COV-96). Setting either one also drops skills
    // that carry no value on that axis — that exclusion is the spec, not a side effect.
    asset_class?: SkillAssetClass
    workflow?: SkillWorkflow
    // 'newest' = created_at DESC across every scope, which is what the Skill Library
    // shows. Omitted keeps the long-standing official-first grouping other callers rely on.
    sort?: 'newest' | 'oldest' | 'name_asc' | 'name_desc'
    // Landing curation (PRD #3680). Only ever true — the server narrows, never inverts.
    show_on_main?: boolean
    // Drop the skills this user hid from their Skill Library (COV-401).
    exclude_hidden?: boolean
  }) =>
  async () => {
    return await responseWrapper<WorkspaceSkillsResponse>(() =>
      API.get<WorkspaceSkillsResponse>(`${alphaPath}/workspace_skills`, { params })
    )
  }

// Admin-managed workflow vocabulary for the Skill Library (filter options and labels).
export const getSkillWorkflows = () => async () => {
  return await responseWrapper<{ data: SkillWorkflowOption[] }>(() =>
    API.get<{ data: SkillWorkflowOption[] }>(`${alphaPath}/skill_workflows`)
  )
}

export const getHiddenWorkspaceSkillIds = () => async () => {
  return await responseWrapper<{ data: string[] }>(() =>
    API.get<{ data: string[] }>(`${alphaPath}/workspace_skills/hidden_ids`)
  )
}

export const getWorkspaceSkill = (id: string) => async () => {
  return await responseWrapper<WorkspaceSkill>(() =>
    API.get<WorkspaceSkill>(`${alphaPath}/workspace_skills/${id}`)
  )
}

export const getWorkspaceSkillContent = (id: string) => async () => {
  return await responseWrapper<{ content: string }>(() =>
    API.get<{ content: string }>(`${alphaPath}/workspace_skills/${id}/content`)
  )
}

// The current user's own skill folders (PRD #3680) — used by the My Folders manager and to render
// favorited folders on the Analytics main page.
export const getSkillGroups = () => async () => {
  return await responseWrapper<SkillGroupsResponse>(() =>
    API.get<SkillGroupsResponse>(`${alphaPath}/skill_groups`)
  )
}

// STEP1 (PRD #3680): read-only org admin-configured default folders shown on the main page.
export const getOrgDefaultSkillFolders = () => async () => {
  return await responseWrapper<SkillGroupsResponse>(() =>
    API.get<SkillGroupsResponse>(`${alphaPath}/skill_groups/org_defaults`)
  )
}

// Compliance Meeting Summaries
export const getComplianceMeetingSummaries =
  (page: number, filters: ComplianceMeetingSummaryFilters = {}) =>
  async () => {
    const params = {
      page,
      ...Object.fromEntries(
        Object.entries(filters).filter(([, value]) => value !== '' && value !== undefined)
      ),
    }
    return await responseWrapper<ComplianceMeetingSummaryListResponse>(() =>
      API.get<ComplianceMeetingSummaryListResponse>(`${alphaPath}/compliance/meeting_summaries`, {
        params,
      })
    )
  }

export const getComplianceMeetingSummaryDetail = (id: string) => async () => {
  return await responseWrapper<{ data: ComplianceMeetingSummaryDetail }>(() =>
    API.get<{ data: ComplianceMeetingSummaryDetail }>(
      `${alphaPath}/compliance/meeting_summaries/${id}`
    )
  )
}

// FactSet OAuth connector (PRD #2716) ----------------------------------------
// alpha-server `/oauth/factset/status` returns 404 for non-enabled
// organizations — the caller treats that as "feature unavailable" and hides
// the UI rather than surfacing an error. We pass isAlert=false to
// responseWrapper so the 404 does not pop a global toast.

export const getFactsetOauthStatus = () => async () => {
  return await responseWrapper<{ data: FactsetOauthStatus }>(
    () => API.get<{ data: FactsetOauthStatus }>(`${alphaPath}/oauth/factset/status`),
    false
  )
}

// Completes the OAuth dance. Client receives `?code=...&state=...` from
// FactSet, then GETs alpha-server's callback to exchange the code for
// tokens. Auth flows over the user's existing JWT cookie.
export const completeFactsetOauthCallback = (params: FactsetOauthCallbackParams) => async () => {
  return await responseWrapper<{ data: FactsetOauthStatus }>(() =>
    API.get<{ data: FactsetOauthStatus }>(`${alphaPath}/oauth/factset/callback`, {
      params,
    })
  )
}

// LIN-721 — Microsoft (Teams) connector, mirrors FactSet above.
export const getMicrosoftOauthStatus = (connector: MicrosoftConnector) => async () => {
  return await responseWrapper<{ data: MicrosoftOauthStatus }>(() =>
    API.get<{ data: MicrosoftOauthStatus }>(`${alphaPath}/oauth/microsoft/status`, {
      params: { connector },
    })
  )
}

// isAlert=false — the callback page renders exchange failures itself; a
// global toast on top of that would be a duplicate message.
export const completeMicrosoftOauthCallback =
  (params: MicrosoftOauthCallbackParams) => async () => {
    return await responseWrapper<{ data: MicrosoftOauthStatus }>(
      () =>
        API.get<{ data: MicrosoftOauthStatus }>(`${alphaPath}/oauth/microsoft/callback`, {
          params,
        }),
      false
    )
  }

// --- Alert Agent (Trigger Layer) ---

export const getTriggers = () => async () => {
  return await responseWrapper<TriggerListResponse>(() =>
    API.get<TriggerListResponse>(`${alphaPath}/triggers`)
  )
}

export const getTrigger = (id: string) => async () => {
  return await responseWrapper<{ trigger: Trigger }>(() =>
    API.get<{ trigger: Trigger }>(`${alphaPath}/triggers/${id}`)
  )
}

export const getTriggerEvents =
  (query: TriggerEventsQuery = {}) =>
  async () => {
    return await responseWrapper<TriggerEventListResponse>(() =>
      API.get<TriggerEventListResponse>(`${alphaPath}/trigger_events`, { params: query })
    )
  }

export const getTriggerEvent = (id: string) => async () => {
  return await responseWrapper<TriggerEventDetailResponse>(() =>
    API.get<TriggerEventDetailResponse>(`${alphaPath}/trigger_events/${id}`)
  )
}

// Evidence bytes are proxied by alpha (not a presigned S3 URL): the endpoint
// re-checks ownership per request and streams the object. It must therefore go
// through the authenticated axios client — a bare <img src> would resolve
// against the Next.js origin and 404. `isAlert = false`: a missing screenshot
// renders its own inline fallback instead of a global toast.
export const getTriggerEventEvidence = (id: string, kind: string) => async () => {
  return await responseWrapper<Blob>(
    () =>
      API.get<Blob>(`${alphaPath}/trigger_events/${id}/evidence/${kind}`, {
        responseType: 'blob',
      }),
    false
  )
}

// --- Ontology (linq-alpha-server #3340) ---
// Org-scoped class/property/entity data backing the dynamic Fund selector.

export type OntologyListResponse<T> = { total: number; data: T[] }

// Classes/properties are unpaginated on the client, so request the server's max
// page size (200) to avoid silently truncating at the default (50) if an org
// ever has that many classes, or a class that many properties.
const ONTOLOGY_META_PER_PAGE = 200

export const getOntologyClasses = () => async () => {
  const res = await responseWrapper<OntologyListResponse<OntologyClass>>(() =>
    API.get<OntologyListResponse<OntologyClass>>(`${alphaPath}/ontology_classes`, {
      params: { per: ONTOLOGY_META_PER_PAGE },
    })
  )
  return res.data
}

export type OntologyPropertiesResponse = OntologyListResponse<OntologyProperty> & {
  filter_options?: OntologyFilterOptions
}

export const getOntologyProperties = (ontology_class_id: string) => async () =>
  responseWrapper<OntologyPropertiesResponse>(() =>
    API.get<OntologyPropertiesResponse>(`${alphaPath}/ontology_properties`, {
      params: { ontology_class_id, per: ONTOLOGY_META_PER_PAGE },
    })
  )

export const getOntologyEntitiesPage =
  (params: {
    ontology_class_id: string
    page: number
    per: number
    query?: string
    attrs?: Record<string, string>
  }) =>
  async () =>
    responseWrapper<OntologyListResponse<OntologyEntity>>(() =>
      API.get<OntologyListResponse<OntologyEntity>>(`${alphaPath}/ontology_entities`, { params })
    )

// --- Workspace model options (linq-alpha-server) ---
// Selectable LLM models for the analytics model chip, sourced from the DPC
// ModelPolicy allow-list of the profile `mode` resolves to. `isAlert: false`
// keeps a failed lookup from raising a toast — the chip just hides.
export const getWorkspaceModelOptions = (mode: string) => async () =>
  responseWrapper<WorkspaceModelOptionsResponse>(
    () =>
      API.get<WorkspaceModelOptionsResponse>(`${alphaPath}/workspace_model_options`, {
        params: { mode },
      }),
    false
  )

// --- Web Monitor (Website Watch) ---

// per_page pinned to the server cap: this list has no pagination UI, and the
// server default is 25 while a user may own up to Trigger::MAX_ACTIVE_TRIGGERS_PER_USER
// (100), so without it watches 26+ silently disappear from the page.
export const getWebWatches = () => async () => {
  return await responseWrapper<WebWatchListResponse>(() =>
    API.get<WebWatchListResponse>(`${alphaPath}/web_watches`, { params: { per_page: 100 } })
  )
}

export const getWebWatch = (id: string) => async () => {
  return await responseWrapper<WebWatchShowResponse>(() =>
    API.get<WebWatchShowResponse>(`${alphaPath}/web_watches/${id}`)
  )
}

// LIN-1056 — Slack connector, mirrors Microsoft above. One connector, so no
// `connector` parameter on any of these calls.
export const getSlackOauthStatus = () => async () => {
  return await responseWrapper<{ data: SlackOauthStatus }>(() =>
    API.get<{ data: SlackOauthStatus }>(`${alphaPath}/oauth/slack/status`)
  )
}

// LIN-1573 — workspace install. Asked before the user has connected their own
// Slack account, so it is keyed by organization on the server, not by team.
export const getSlackInstallStatus = () => async () => {
  return await responseWrapper<{ data: SlackInstallStatus }>(() =>
    API.get<{ data: SlackInstallStatus }>(`${alphaPath}/oauth/slack/install/status`)
  )
}

// isAlert=false — same reason as the connector callback below: the install
// callback page renders its own failure.
export const completeSlackInstallCallback = (params: SlackInstallCallbackParams) => async () => {
  return await responseWrapper<{ data: SlackInstallStatus }>(
    () =>
      API.get<{ data: SlackInstallStatus }>(`${alphaPath}/oauth/slack/install/callback`, {
        params,
      }),
    false
  )
}

// isAlert=false — the callback page renders exchange failures itself; a
// global toast on top of that would be a duplicate message.
export const completeSlackOauthCallback = (params: SlackOauthCallbackParams) => async () => {
  return await responseWrapper<{ data: SlackOauthStatus }>(
    () => API.get<{ data: SlackOauthStatus }>(`${alphaPath}/oauth/slack/callback`, { params }),
    false
  )
}
